// Споделена презентация на разбивки по метод на плащане (Дашборд, Справка по
// сервитьори, …): амбър палитра за сегментите и построяване на записите от
// активните методи.
import type { PaymentMethod } from "../api/paymentMethods";

// Амбър палитра за сегментите по метод на плащане — редува тъмно/светло,
// за да са различими съседните сегменти в лентата (без нюанси извън системата).
export const METHOD_COLORS = [
  "#D97706", // brand
  "#FEE8C4", // brandTint2
  "#B45309", // brandDeep
  "#FDCF8F",
  "#8C5A0F",
  "#F0A63F",
];
export const methodColor = (i: number) =>
  METHOD_COLORS[i % METHOD_COLORS.length];

export interface MethodEntry {
  id: number;
  label: string;
  color: string;
  amount: number;
  count: number;
  pct: number;
}

// Кутийка за всеки АКТИВЕН метод (същите, които се виждат в Маркирането при
// закриване) + всеки неактивен метод с пари в периода (за да не „изчезват"
// суми при изключен метод). Процентът е спрямо total.
export function buildMethodEntries({
  activeMethods,
  allMethods,
  totals,
  total,
  isBg,
}: {
  activeMethods: PaymentMethod[];
  allMethods: PaymentMethod[];
  totals: Map<number, { amount: number; count: number }>;
  total: number;
  isBg: boolean;
}): MethodEntry[] {
  const pmName = (id: number): string => {
    const pm =
      allMethods.find((m) => m.id === id) ??
      activeMethods.find((m) => m.id === id);
    return pm ? (isBg ? pm.name_bg : pm.name_en) : `#${id}`;
  };

  const defs: { id: number; label: string }[] = [...activeMethods]
    .sort((a, b) => a.sort_index - b.sort_index || a.id - b.id)
    .map((m) => ({ id: m.id, label: isBg ? m.name_bg : m.name_en }));
  const known = new Set(defs.map((d) => d.id));
  for (const [id, v] of totals) {
    if (!known.has(id) && v.amount > 0) {
      defs.push({ id, label: pmName(id) });
      known.add(id);
    }
  }
  return defs.map((d, i) => {
    const v = totals.get(d.id) ?? { amount: 0, count: 0 };
    return {
      ...d,
      color: methodColor(i),
      amount: v.amount,
      count: v.count,
      pct: total > 0 ? (v.amount / total) * 100 : 0,
    };
  });
}

// Ред от BE payment_breakdown (персонални отчети / отчети на заведението) —
// имената идват готови от BE-то, редът е по sort_index.
export interface PaymentBreakdownLike {
  payment_method_id: number;
  name_bg: string;
  name_en: string;
  amount: number | string;
  bills_count: number | string;
}

// Строи MethodEntry[] от payment_breakdown списъци (по един на отчет):
// сумира по метод, пази реда на първата поява (BE вече сортира по sort_index).
export function buildEntriesFromBreakdowns(
  lists: (PaymentBreakdownLike[] | undefined)[],
  total: number,
  isBg: boolean,
): MethodEntry[] {
  const map = new Map<
    number,
    { label: string; amount: number; count: number; order: number }
  >();
  let orderCounter = 0;
  for (const list of lists) {
    for (const b of list ?? []) {
      const cur = map.get(b.payment_method_id) ?? {
        label: isBg ? b.name_bg : b.name_en,
        amount: 0,
        count: 0,
        order: orderCounter++,
      };
      cur.amount += Number(b.amount);
      cur.count += Number(b.bills_count);
      map.set(b.payment_method_id, cur);
    }
  }
  return [...map.entries()]
    .sort((a, b) => a[1].order - b[1].order || a[0] - b[0])
    .map(([id, v], i) => ({
      id,
      label: v.label,
      color: methodColor(i),
      amount: v.amount,
      count: v.count,
      pct: total > 0 ? (v.amount / total) * 100 : 0,
    }));
}
